import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Calendar, LayoutGrid, CalendarDays, Home } from 'lucide-react';
import './App.css';


const MOCK_MOODS = (() => {
  const colors = [
    { color: '#F5D55A', label: 'happy' },
    { color: '#F2ACAC', label: 'peaceful' },
    { color: '#8FBFBF', label: 'stressed' },
    { color: '#9EB5CC', label: 'sad' },
    { color: '#96C896', label: 'accomplished' },
    { color: '#BAB0DC', label: 'sick/tired' },
    { color: '#C2C0BB', label: 'neutral' },
    { color: '#F08080', label: 'angry' },
  ];
  const notes = {
    happy: ['Really good day overall!', 'Feeling great ✨', 'Amazing energy today', 'Laughed a lot today 😊'],
    peaceful: ['Slow morning, loved it', 'Nice walk outside', 'Quiet and calm', 'Read a book all afternoon'],
    stressed: ['Lots on my plate today', 'Deadline crunch 😰', 'Too many things at once', ''],
    sad: ['Missing people', 'Just felt low today', 'Hard day emotionally', ''],
    accomplished: ['Finished a big project!', 'Hit my goals today 💪', 'Super productive', 'Knocked out my to-do list'],
    'sick/tired': ['Barely got out of bed', 'Under the weather', 'So exhausted', ''],
    neutral: ['Normal day', 'Nothing special', 'Average', ''],
    angry: ['Frustrated with everything', 'Really irritating day', ''],
  };
  const weights = [3, 3, 2, 1, 3, 1, 2, 1]; // bias toward positive moods
  const data = {};
  const start = new Date(2026, 0, 1);
  const end = new Date(2026, 3, 30);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    if (Math.random() < 0.12) continue; // ~12% days skipped
    const roll = Math.random() * weights.reduce((a, b) => a + b, 0);
    let cum = 0, idx = 0;
    for (let i = 0; i < weights.length; i++) { cum += weights[i]; if (roll < cum) { idx = i; break; } }
    const { color, label } = colors[idx];
    const pool = notes[label];
    const note = pool[Math.floor(Math.random() * pool.length)];
    const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    data[key] = { color, label, note };
  }
  return data;
})();

const MoodYearRecap = () => {
  const [moods, setMoods] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showPixelView, setShowPixelView] = useState(false);
  const [currentNote, setCurrentNote] = useState('');
  const [hoveredPixel, setHoveredPixel] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [currentWeekStart, setCurrentWeekStart] = useState(null);
  const [weather, setWeather] = useState({ denied: true });
  const [showCalendarView, setShowCalendarView] = useState(false);
  const [showDateDetail, setShowDateDetail] = useState(false);
  const [detailDate, setDetailDate] = useState(null);
  const [showMockBtn, setShowMockBtn] = useState(true);
  const [insightMonth, setInsightMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [gridScale, setGridScale] = useState(1.25);

  // Mood colors with WCAG AA compliance
  const moodColors = [
    { name: 'Default', color: '#C2C0BB', label: 'neutral' },
    { name: 'Happy', color: '#F5D55A', label: 'happy' },
    { name: 'Peaceful', color: '#F2ACAC', label: 'peaceful' },
    { name: 'Stressed', color: '#8FBFBF', label: 'stressed' },
    { name: 'Sad', color: '#9EB5CC', label: 'sad' },
    { name: 'Angry', color: '#F08080', label: 'angry' },
    { name: 'Sick/Tired', color: '#BAB0DC', label: 'sick/tired' },
    { name: 'Accomplished', color: '#96C896', label: 'accomplished' },
  ];

  const commonEmojis = ['😊', '😂', '🥰', '😍', '🤗', '😌', '😔', '😢', '😭', '😤', '😡', '🤯', '😰', '🤒', '🤕', '😴', '🎉', '✨', '💪', '👍', '❤️', '💙', '🌟', '🔥', '☀️', '🌧️', '⭐'];

  // Load moods from storage
  useEffect(() => {
    const loadMoods = async () => {
      try {
        const result = await window.storage.get('moods-2026');
        if (result && result.value) {
          setMoods(JSON.parse(result.value));
        }
      } catch (error) {
        console.log('No previous mood data');
      }
    };
    loadMoods();

    // Set initial week
    const today = new Date();
    const dayOfWeek = today.getDay();
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - dayOfWeek);
    setCurrentWeekStart(weekStart);

    // Update current date every minute
    const timer = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      try {
        const [weatherRes, geoRes] = await Promise.all([
          fetch(`https://api.open-meteo.com/v1/forecast?latitude=${coords.latitude}&longitude=${coords.longitude}&current=temperature_2m,weather_code&temperature_unit=fahrenheit`),
          fetch(`https://nominatim.openstreetmap.org/reverse?lat=${coords.latitude}&lon=${coords.longitude}&format=json`),
        ]);
        const weatherData = await weatherRes.json();
        const geoData = await geoRes.json();
        const code = weatherData.current.weather_code;
        const temp = Math.round(weatherData.current.temperature_2m);
        const city = geoData.address.city || geoData.address.town || geoData.address.village || geoData.address.county || '';
        const isoRegion = geoData.address['ISO3166-2-lvl4'] || '';
        const stateAbbr = isoRegion.includes('-') ? isoRegion.split('-')[1] : (geoData.address.state || '');
        const emoji = code === 0 ? '☀️' : code <= 3 ? '⛅' : code <= 48 ? '🌫️' : code <= 67 ? '🌧️' : code <= 77 ? '❄️' : code <= 82 ? '🌦️' : code <= 86 ? '❄️' : '⛈️';
        setWeather({ emoji, temp, location: [city, stateAbbr].filter(Boolean).join(', ') });
      } catch (e) {
        setWeather({ denied: true });
      }
    }, () => setWeather({ denied: true }));
  }, []);

  useEffect(() => {
    const NATURAL_W = 209;
    const NATURAL_H = 484;
    const update = () => {
      if (window.innerWidth <= 480) {
        // headerH: 32px top-padding + 30px pill + 14px margin + 20px title = ~96px, +4px grid-outer top padding
        const headerH = 100;
        // navH: full-width bar ~60px tall + safe area inset ~34px on notched phones + breathing room
        const navH = 100;
        const availW = window.innerWidth - 40;
        const availH = window.innerHeight - headerH - navH;
        const scaleW = availW / NATURAL_W;
        const scaleH = availH / NATURAL_H;
        setGridScale(Math.min(scaleW, scaleH) * 0.88);
      } else {
        setGridScale(1.1);
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const saveMoods = async (newMoods) => {
    try {
      await window.storage.set('moods-2026', JSON.stringify(newMoods));
    } catch (error) {
      console.error('Error saving moods:', error);
    }
  };

  const loadMockData = async () => {
    setMoods(MOCK_MOODS);
    await saveMoods(MOCK_MOODS);
  };

  const renderMockBtn = () => showMockBtn ? (
    <div style={{
      position: 'fixed',
      bottom: '100px',
      right: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      zIndex: 200,
    }}>
      <button onClick={loadMockData} style={{
        fontSize: '11px',
        color: '#787774',
        backgroundColor: '#F5F5F4',
        border: '1px solid #E9E9E7',
        borderRadius: '20px',
        padding: '5px 10px',
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}>mock data</button>
      <button onClick={() => setShowMockBtn(false)} style={{
        fontSize: '11px',
        color: '#AEACA8',
        backgroundColor: '#F5F5F4',
        border: '1px solid #E9E9E7',
        borderRadius: '50%',
        width: '20px',
        height: '20px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        fontFamily: 'inherit',
      }}>×</button>
    </div>
  ) : null;

  const formatDateKey = (date) => {
    if (!date) return null;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const getDayColor = (date) => {
    if (!date) return null;
    const dateKey = formatDateKey(date);
    const data = moods[dateKey];
    
    if (typeof data === 'string') {
      return data;
    } else if (data && typeof data === 'object' && data.color) {
      return data.color;
    }
    return null;
  };

  const getMoodLabel = (color) => {
    return moodColors.find(m => m.color === color)?.label || null;
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = currentDate;
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isFutureDate = (date) => {
    if (!date) return false;
    return date > currentDate;
  };

  const handleDayClick = (date) => {
    if (!date || isFutureDate(date)) return;
    setSelectedDate(date);
    
    const dateKey = formatDateKey(date);
    const existingData = moods[dateKey];
    if (existingData && typeof existingData === 'object') {
      setCurrentNote(existingData.note || '');
      setSelectedColor(existingData.color || null);
    } else if (typeof existingData === 'string') {
      setSelectedColor(existingData);
      setCurrentNote('');
    } else {
      setCurrentNote('');
      setSelectedColor(null);
    }
    
    setShowPicker(true);
  };

  const handleColorSelect = (color) => {
    setSelectedColor(color);
  };

  const handleSave = async () => {
    if (!selectedDate || !selectedColor) return;
    
    const dateKey = formatDateKey(selectedDate);
    const newMoods = { 
      ...moods, 
      [dateKey]: {
        color: selectedColor,
        note: currentNote.trim()
      }
    };
    setMoods(newMoods);
    saveMoods(newMoods);
    
    handleClose();
  };

  const handleClose = () => {
    setShowPicker(false);
    setSelectedDate(null);
    setCurrentNote('');
    setSelectedColor(null);
    setShowEmojiPicker(false);
  };

  const handleDayView = (date) => {
    if (!date || isFutureDate(date)) return;
    const dateKey = formatDateKey(date);
    const existing = moods[dateKey];
    const hasData = existing && (typeof existing === 'string' ? existing : existing.color);
    if (hasData && !isToday(date)) {
      setDetailDate(date);
      setShowDateDetail(true);
    } else {
      handleDayClick(date);
    }
  };

  const handleClear = () => {
    setCurrentNote('');
    setSelectedColor(null);
    
    if (selectedDate) {
      const dateKey = formatDateKey(selectedDate);
      const newMoods = { ...moods };
      delete newMoods[dateKey];
      setMoods(newMoods);
      saveMoods(newMoods);
    }
  };

  const addEmoji = (emoji) => {
    setCurrentNote(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  // Get week dates
  const getWeekDates = () => {
    if (!currentWeekStart) return [];
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const navigateWeek = (direction) => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newWeekStart);
  };

  const getTodayMood = () => {
    const todayKey = formatDateKey(currentDate);
    const todayData = moods[todayKey];
    if (typeof todayData === 'string') return todayData;
    if (todayData && todayData.color) return todayData.color;
    return null;
  };

  const weekDates = getWeekDates();
  const todayMoodColor = getTodayMood();
  const todayMoodLabel = todayMoodColor ? getMoodLabel(todayMoodColor) : null;

  const isMobile = window.innerWidth <= 480;

  const renderBottomNav = (activeView) => {
    const items = [
      { label: 'Calendar', icon: isMobile ? <Calendar size={22} /> : <Calendar size={18} />, view: 'calendar', onClick: () => { setShowPixelView(false); setShowCalendarView(true); } },
      { label: 'Home',     icon: isMobile ? <Home size={22} />     : <Home size={20} />,     view: 'home',     onClick: () => { setShowPixelView(false); setShowCalendarView(false); } },
      { label: 'Full Year',icon: isMobile ? <LayoutGrid size={22}/> : <LayoutGrid size={18}/>,view: 'year',     onClick: () => { setShowCalendarView(false); setShowPixelView(true); } },
    ];

    if (isMobile) {
      return (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #E9E9E7',
          display: 'flex',
          alignItems: 'flex-start',
          zIndex: 200,
          paddingBottom: 'env(safe-area-inset-bottom, 16px)',
        }}>
          {items.map(({ label, icon, view, onClick }) => {
            const isActive = activeView === view;
            return (
              <button
                key={view}
                onClick={onClick}
                style={{
                  flex: 1,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  padding: '10px 0 8px',
                  color: isActive ? '#37352F' : '#9B9A97',
                  transition: 'color 0.15s ease',
                  fontFamily: 'inherit',
                }}
              >
                {icon}
                <span style={{ fontSize: '10px', fontWeight: isActive ? '600' : '500' }}>{label}</span>
              </button>
            );
          })}
        </div>
      );
    }

    const navBtn = (label, icon, view, onClick) => {
      const isActive = activeView === view;
      return (
        <button
          key={view}
          onClick={onClick}
          className="bottom-nav-btn"
          style={{
            background: isActive ? '#F5F5F4' : 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '3px',
            padding: '7px 14px',
            borderRadius: '46px',
            color: isActive ? '#37352F' : '#9B9A97',
            transition: 'all 0.15s ease',
            minWidth: '64px',
          }}
        >
          {icon}
          <span style={{ fontSize: '11px', fontWeight: isActive ? '600' : '500' }}>{label}</span>
        </button>
      );
    };
    return (
      <div className="bottom-nav">
        {navBtn('Calendar', <Calendar size={18} />, 'calendar', () => { setShowPixelView(false); setShowCalendarView(true); })}
        {navBtn('Home', <Home size={20} />, 'home', () => { setShowPixelView(false); setShowCalendarView(false); })}
        {navBtn('Full Year', <LayoutGrid size={18} />, 'year', () => { setShowCalendarView(false); setShowPixelView(true); })}
      </div>
    );
  };

  // Pixel view (unchanged from original)
  if (showPixelView) {
    const monthAbbreviations = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#FFFFFF',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        paddingBottom: '100px',
      }}>
        <div style={{ padding: `${window.innerWidth <= 480 ? 32 : 20}px 16px` }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }}>
            <div style={{
              backgroundColor: '#FFFFFF', border: '1px solid #E9E9E7', borderRadius: '50px',
              padding: '6px 16px', display: 'inline-flex', alignItems: 'center', gap: '6px',
              fontSize: '13px', fontWeight: '500', color: '#37352F',
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
            }}>
              <span style={{ color: '#787774' }}>
                {currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              <span style={{ color: '#787774' }}>
                {currentDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </span>
              {!weather?.denied && (
                <>
                  <span style={{ color: '#D3D3D1' }}>•</span>
                  <span>{weather.emoji}</span>
                  <span>{weather.temp}°F</span>
                  {weather.location && (<><span style={{ color: '#D3D3D1' }}>•</span><span style={{ color: '#787774' }}>{weather.location}</span></>)}
                </>
              )}
              {weather?.denied && (
                <><span style={{ color: '#D3D3D1' }}>•</span><span style={{ fontStyle: 'italic', color: '#9B9A97' }}>enable location permissions</span></>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#37352F', margin: 0 }}>
              {currentDate.getFullYear()} in Pixels
            </h2>
          </div>
        </div>

        <div className="pixel-grid-outer" style={{ animation: 'fadeIn 0.8s ease-out 0.2s both' }}>
          {/* height reservation wrapper — natural width so transform-origin:top center stays centered */}
          <div style={{ height: `${Math.round(480 * gridScale)}px` }}>
          <div className="pixel-grid-scaler" style={{
            display: 'flex',
            flexDirection: 'column',
            transform: `scale(${gridScale})`,
            transformOrigin: 'top center',
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '30px repeat(12, 14px)',
              gap: '1px',
              marginBottom: '1px',
            }}>
              <div></div>
              {monthAbbreviations.map((month, i) => (
                <div key={i} style={{
                  fontSize: '9px',
                  fontWeight: '600',
                  color: '#37352F',
                  textAlign: 'center',
                  padding: '4px 0',
                }}>
                  {month}
                </div>
              ))}
            </div>

            {Array.from({ length: 31 }, (_, dayIndex) => {
              const day = dayIndex + 1;
              return (
                <div key={day} style={{
                  display: 'grid',
                  gridTemplateColumns: '30px repeat(12, 14px)',
                  gap: '1px',
                  marginBottom: '1px',
                }}>
                  <div style={{
                    fontSize: '9px',
                    fontWeight: '500',
                    color: '#9B9A97',
                    textAlign: 'right',
                    padding: '0 6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}>
                    {day}
                  </div>

                  {Array.from({ length: 12 }, (_, monthIndex) => {
                    const year = 2026;
                    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
                    
                    if (day <= daysInMonth) {
                      const date = new Date(year, monthIndex, day);
                      const dateKey = formatDateKey(date);
                      const moodData = moods[dateKey];
                      const color = typeof moodData === 'string' ? moodData : (moodData?.color || null);
                      const note = typeof moodData === 'object' ? moodData?.note : null;
                      const isCurrentDay = isToday(date);
                      const isFuture = isFutureDate(date);
                      
                      const moodLabel = color ? moodColors.find(m => m.color === color)?.label : null;
                      
                      return (
                        <div
                          key={monthIndex}
                          style={{
                            width: '14px',
                            height: '14px',
                            backgroundColor: color || (isFuture ? '#F5F5F4' : (isCurrentDay ? '#FFFFFF' : '#FFFFFF')),
                            border: isCurrentDay && !color ? '1.5px solid #D3D3D1' : (color || !isCurrentDay ? '0.5px solid #E9E9E7' : '1.5px solid #D3D3D1'),
                            borderRadius: '2px',
                            transition: 'all 0.15s ease',
                            cursor: color ? 'pointer' : 'default',
                            position: 'relative',
                          }}
                          onMouseEnter={() => {
                            if (color) {
                              setHoveredPixel({
                                date: date,
                                color: color,
                                label: moodLabel,
                                note: note,
                                x: monthIndex,
                                y: day
                              });
                            }
                          }}
                          onMouseLeave={() => setHoveredPixel(null)}
                        />
                      );
                    }
                    
                    return (
                      <div
                        key={monthIndex}
                        style={{
                          width: '14px',
                          height: '14px',
                          backgroundColor: 'transparent',
                        }}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
          </div>
        </div>

        {hoveredPixel && (
          <div style={{
            position: 'fixed',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#FFFFFF',
            border: '1px solid #E9E9E7',
            borderRadius: '8px',
            padding: '16px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            zIndex: 9999,
            minWidth: '220px',
            maxWidth: '320px',
            pointerEvents: 'none',
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#37352F',
              marginBottom: '8px',
            }}>
              {hoveredPixel.date.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
            {hoveredPixel.label && (
              <div style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '12px',
                backgroundColor: hoveredPixel.color,
                color: '#FFFFFF',
                fontSize: '12px',
                fontWeight: '500',
                marginBottom: hoveredPixel.note ? '8px' : '0',
              }}>
                {hoveredPixel.label}
              </div>
            )}
            {hoveredPixel.note && (
              <div style={{
                fontSize: '13px',
                color: '#787774',
                lineHeight: '1.5',
                marginTop: '8px',
              }}>
                {hoveredPixel.note}
              </div>
            )}
          </div>
        )}

        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}</style>
        {renderBottomNav('year')}
        {renderMockBtn()}
      </div>
    );
  }

  // Calendar year view
  if (showCalendarView) {
    const year = 2026;
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const dayHeaders = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

    const getMonthCells = (month) => {
      const firstDay = new Date(year, month, 1);
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const startOffset = firstDay.getDay(); // Sun-first (0=Sun)
      const cells = [];
      for (let i = 0; i < startOffset; i++) cells.push({ inMonth: false });
      for (let d = 1; d <= daysInMonth; d++) {
        cells.push({ day: d, inMonth: true, date: new Date(year, month, d) });
      }
      while (cells.length % 7 !== 0) cells.push({ inMonth: false });
      return cells;
    };

    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#FFFFFF',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        paddingBottom: '100px',
      }}>
        <div style={{ padding: `${window.innerWidth <= 480 ? 32 : 20}px 16px` }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }}>
            <div style={{
              backgroundColor: '#FFFFFF', border: '1px solid #E9E9E7', borderRadius: '50px',
              padding: '6px 16px', display: 'inline-flex', alignItems: 'center', gap: '6px',
              fontSize: '13px', fontWeight: '500', color: '#37352F',
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
            }}>
              <span style={{ color: '#787774' }}>
                {currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
              <span style={{ color: '#787774' }}>
                {currentDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </span>
              {!weather?.denied && (
                <>
                  <span style={{ color: '#D3D3D1' }}>•</span>
                  <span>{weather.emoji}</span>
                  <span>{weather.temp}°F</span>
                  {weather.location && (<><span style={{ color: '#D3D3D1' }}>•</span><span style={{ color: '#787774' }}>{weather.location}</span></>)}
                </>
              )}
              {weather?.denied && (
                <><span style={{ color: '#D3D3D1' }}>•</span><span style={{ fontStyle: 'italic', color: '#9B9A97' }}>enable location permissions</span></>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#37352F', margin: 0 }}>
              {currentDate.getFullYear()}
            </h2>
          </div>
        </div>

        {/* Month grid */}
        <div style={{ padding: '32px 80px 0', width: '100%', boxSizing: 'border-box' }}>
        <div className="year-calendar-grid">
          {monthNames.map((monthName, mi) => {
            const cells = getMonthCells(mi);
            return (
              <div key={mi}>
                <div style={{
                  fontSize: '9px',
                  fontWeight: '600',
                  color: '#37352F',
                  textAlign: 'center',
                  marginBottom: '4px',
                }}>
                  {monthName}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
                  {dayHeaders.map((d, i) => (
                    <div key={i} style={{
                      textAlign: 'center',
                      fontSize: '6px',
                      fontWeight: '500',
                      color: '#9B9A97',
                      paddingBottom: '2px',
                    }}>
                      {d.charAt(0)}
                    </div>
                  ))}
                  {cells.map((cell, ci) => {
                    if (!cell.inMonth) {
                      return <div key={ci} style={{ aspectRatio: '1' }} />;
                    }
                    const dateKey = formatDateKey(cell.date);
                    const moodData = moods[dateKey];
                    const color = typeof moodData === 'string' ? moodData : moodData?.color;
                    const todayDay = isToday(cell.date);
                    const future = isFutureDate(cell.date);
                    return (
                      <div
                        key={ci}
                        onClick={() => !future && handleDayView(cell.date)}
                        title={color ? getMoodLabel(color) : ''}
                        style={{
                          aspectRatio: '1',
                          borderRadius: '3px',
                          backgroundColor: color || '#FFFFFF',
                          border: todayDay
                            ? '2px solid #37352F'
                            : color
                              ? 'none'
                              : '1px solid #E9E9E7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '8px',
                          fontWeight: todayDay ? '700' : '400',
                          color: color ? '#FFFFFF' : '#37352F',
                          cursor: future ? 'default' : 'pointer',
                          opacity: future ? 0.3 : 1,
                          transition: 'all 0.12s ease',
                          boxSizing: 'border-box',
                          boxShadow: color ? '0 1px 3px rgba(0,0,0,0.10)' : 'none',
                        }}
                        onMouseEnter={(e) => {
                          if (!future) e.currentTarget.style.opacity = '0.8';
                        }}
                        onMouseLeave={(e) => {
                          if (!future) e.currentTarget.style.opacity = '1';
                        }}
                      >
                        {cell.day}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        </div>
        {renderBottomNav('calendar')}
        {renderMockBtn()}

        {showDateDetail && detailDate && (() => {
          const dateKey = formatDateKey(detailDate);
          const moodData = moods[dateKey];
          const color = typeof moodData === 'string' ? moodData : moodData?.color;
          const note = moodData && typeof moodData === 'object' ? moodData.note : null;
          const label = getMoodLabel(color);
          return (
            <div onClick={() => setShowDateDetail(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px', animation: 'fadeIn 0.2s ease-out' }}>
              <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', maxWidth: '360px', width: '100%', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.14)', animation: 'slideUp 0.3s ease-out' }}>
                <div style={{ backgroundColor: color || '#E9E9E7', padding: '28px 24px 24px', textAlign: 'center' }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: 'rgba(255,255,255,0.85)', marginBottom: '6px' }}>{detailDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#FFFFFF', textTransform: 'capitalize' }}>{label || 'Mood logged'}</div>
                </div>
                <div style={{ padding: '20px 24px 24px' }}>
                  {note ? <p style={{ fontSize: '14px', color: '#787774', lineHeight: '1.6', fontStyle: 'italic', margin: '0 0 20px', borderLeft: `3px solid ${color || '#E9E9E7'}`, paddingLeft: '12px' }}>"{note}"</p> : <p style={{ fontSize: '13px', color: '#9B9A97', margin: '0 0 20px' }}>No note for this day.</p>}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => { setShowDateDetail(false); handleDayClick(detailDate); }} style={{ flex: 1, padding: '10px', borderRadius: '8px', backgroundColor: '#37352F', color: '#FFFFFF', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => setShowDateDetail(false)} style={{ flex: 1, padding: '10px', borderRadius: '8px', backgroundColor: '#F5F5F4', color: '#37352F', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Close</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {showPicker && selectedDate && (
          <div onClick={handleClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, animation: 'fadeIn 0.2s ease-out', padding: '20px' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#FFFFFF', borderRadius: '12px', padding: '24px', maxWidth: '400px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', animation: 'slideUp 0.3s ease-out' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#37352F', marginBottom: '4px' }}>How was {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}?</h3>
              <p style={{ fontSize: '13px', color: '#787774', marginBottom: '20px' }}>Choose a color that represents your mood.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px' }}>
                {moodColors.map((mood) => (
                  <div key={mood.name} onClick={() => handleColorSelect(mood.color)} style={{ padding: '12px 16px', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', border: selectedColor === mood.color ? '2px solid #37352F' : '1px solid #E9E9E7', backgroundColor: selectedColor === mood.color ? '#F5F5F4' : '#FAFAFA' }}>
                    <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: mood.color, flexShrink: 0, border: '1px solid rgba(0,0,0,0.1)' }} />
                    <span style={{ fontSize: '14px', color: '#37352F', fontWeight: selectedColor === mood.color ? '500' : '400' }}>{mood.label}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '500', color: '#37352F' }}>Notes:</label>
                  <span style={{ fontSize: '11px', color: currentNote.split(/\s+/).filter(w => w).length > 75 ? '#E03E3E' : '#9B9A97' }}>{currentNote.split(/\s+/).filter(w => w).length}/75 words</span>
                </div>
                <textarea value={currentNote} onChange={(e) => setCurrentNote(e.target.value)} placeholder="Add a short note about your day..." style={{ width: '100%', minHeight: '80px', padding: '10px', fontSize: '13px', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", border: '1px solid #E9E9E7', borderRadius: '6px', resize: 'vertical', color: '#37352F', backgroundColor: '#FAFAFA', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px' }}>
                <button onClick={handleClear} style={{ backgroundColor: 'transparent', color: '#787774', border: '1px solid #E9E9E7', padding: '10px 20px', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>Clear</button>
                <button onClick={handleSave} disabled={!selectedColor} style={{ backgroundColor: !selectedColor ? '#E9E9E7' : '#37352F', color: !selectedColor ? '#9B9A97' : '#FFFFFF', border: 'none', padding: '10px 20px', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: !selectedColor ? 'not-allowed' : 'pointer' }}>Save</button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
      </div>
    );
  }

  // Main redesigned view
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#FFFFFF',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      paddingBottom: '100px',
      position: 'relative',
    }}>
      {/* Title header — matches calendar & full year */}
      <div style={{ padding: `${window.innerWidth <= 480 ? 32 : 20}px 16px` }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '14px' }}>
          <div style={{
            backgroundColor: '#FFFFFF', border: '1px solid #E9E9E7', borderRadius: '50px',
            padding: '6px 16px', display: 'inline-flex', alignItems: 'center', gap: '6px',
            fontSize: '13px', fontWeight: '500', color: '#37352F', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          }}>
            <span style={{ color: '#787774' }}>
              {currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
            <span style={{ color: '#787774' }}>
              {currentDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </span>
            {!weather?.denied && (
              <>
                <span style={{ color: '#D3D3D1' }}>•</span>
                <span>{weather.emoji}</span>
                <span>{weather.temp}°F</span>
                {weather.location && (<><span style={{ color: '#D3D3D1' }}>•</span><span style={{ color: '#787774' }}>{weather.location}</span></>)}
              </>
            )}
            {weather?.denied && (
              <><span style={{ color: '#D3D3D1' }}>•</span><span style={{ fontStyle: 'italic', color: '#9B9A97' }}>enable location permissions</span></>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'center', position: 'relative' }}>
          <button onClick={() => navigateWeek(-1)} style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center' }}>
            <ChevronLeft size={20} color="#37352F" />
          </button>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#37352F', margin: 0 }}>
            {(weekDates.find(d => isToday(d)) || weekDates[3])?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
          <button onClick={() => navigateWeek(1)} style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center' }}>
            <ChevronRight size={20} color="#37352F" />
          </button>
        </div>
      </div>

      {/* Week days */}
      <div style={{ padding: '16px 16px 0' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '8px',
        }}>
          {weekDates.map((date, i) => {
            const dayColor = getDayColor(date);
            const isTodayDate = isToday(date);
            const isFuture = isFutureDate(date);
            
            return (
              <div
                key={i}
                onClick={() => !isFuture && handleDayView(date)}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: !isFuture ? 'pointer' : 'default',
                  opacity: isFuture ? 0.4 : 1,
                }}
              >
                <div style={{
                  fontSize: '11px',
                  fontWeight: '500',
                  color: isTodayDate ? '#37352F' : '#9B9A97',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  {date.toLocaleDateString('en-US', { weekday: 'short' }).charAt(0)}
                </div>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: dayColor || '#F5F5F4',
                  border: isTodayDate ? '2px solid #37352F' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: isTodayDate ? '600' : '500',
                  color: dayColor ? '#FFFFFF' : '#37352F',
                  opacity: dayColor && !isTodayDate ? 0.8 : 1,
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  if (!isFuture) {
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isFuture) {
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              >
                {date.getDate()}
              </div>
              {isTodayDate && (
                <div style={{
                  fontSize: '9px',
                  fontWeight: '600',
                  color: '#37352F',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  TODAY
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>

      {/* Hero Gradient Section */}
      <div style={{
        backgroundColor: todayMoodColor || '#D3D3D1',
        position: 'relative',
        padding: '60px 24px 80px',
        textAlign: 'center',
        overflow: 'hidden',
      }}>
        <h3 style={{
          fontSize: '16px',
          fontWeight: '500',
          color: todayMoodColor ? '#FFFFFF' : '#37352F',
          margin: 0,
          marginBottom: '8px',
          opacity: 0.9,
        }}>
          Today's mood:
        </h3>
        <div style={{
          fontSize: '48px',
          fontWeight: '700',
          color: todayMoodColor ? '#FFFFFF' : '#37352F',
          margin: 0,
          marginBottom: '12px',
          textTransform: 'capitalize',
        }}>
          {todayMoodLabel || 'Not logged yet'}
        </div>
        {(() => {
          const todayData = moods[formatDateKey(currentDate)];
          const note = todayData && typeof todayData === 'object' ? todayData.note : null;
          return note ? (
            <p style={{
              fontSize: '14px',
              color: todayMoodColor ? 'rgba(255,255,255,0.82)' : '#787774',
              margin: '0 auto 28px',
              maxWidth: '320px',
              lineHeight: '1.55',
              fontStyle: 'italic',
            }}>
              "{note}"
            </p>
          ) : <div style={{ marginBottom: '32px' }} />;
        })()}
        
        <button
          onClick={() => handleDayClick(currentDate)}
          style={{
            backgroundColor: '#FFFFFF',
            color: todayMoodColor || '#37352F',
            border: 'none',
            padding: '12px 32px',
            borderRadius: '24px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
          }}
        >
          {todayMoodLabel ? 'Edit Today\'s Mood' : 'Log Today\'s Mood'}
        </button>

        <svg style={{ position: 'absolute', bottom: '-1px', left: 0, width: '200%', height: '60px', animation: 'waveSlide 14s linear infinite', pointerEvents: 'none' }}
          viewBox="0 0 2880 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0,20 C480,0 960,50 1440,20 C1920,0 2400,50 2880,20 L2880,60 L0,60 Z" fill="#FFFFFF" />
        </svg>
      </div>


      {/* Insight Cards */}
      {(() => {
        const [insightYr, insightMo] = insightMonth.split('-').map(Number);
        const monthPrefix = insightMonth;
        const yearPrefix = `${currentDate.getFullYear()}-`;

        // Build dropdown options: Jan 2026 → current month
        const monthOptions = [];
        const now = new Date();
        const startYr = 2026, startMo = 1;
        for (let y = startYr; y <= now.getFullYear(); y++) {
          const moStart = y === startYr ? startMo : 1;
          const moEnd = y === now.getFullYear() ? now.getMonth() + 1 : 12;
          for (let m = moStart; m <= moEnd; m++) {
            const val = `${y}-${String(m).padStart(2, '0')}`;
            const label = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            monthOptions.push({ val, label });
          }
        }
        monthOptions.reverse();

        // Monthly mood counts
        const monthCounts = {};
        Object.entries(moods).forEach(([k, v]) => {
          if (!k.startsWith(monthPrefix)) return;
          const c = typeof v === 'string' ? v : v?.color;
          if (c) monthCounts[c] = (monthCounts[c] || 0) + 1;
        });
        const sortedMonth = Object.entries(monthCounts).sort((a, b) => b[1] - a[1]);
        const maxCount = sortedMonth[0]?.[1] || 1;

        // Trends
        let streak = 0;
        const d = new Date(currentDate);
        while (true) {
          const k = formatDateKey(d);
          const v = moods[k];
          if (v && (typeof v === 'string' ? v : v?.color)) { streak++; d.setDate(d.getDate() - 1); }
          else break;
        }
        const totalLogged = Object.keys(moods).filter(k => k.startsWith(yearPrefix)).length;
        const allCounts = {};
        Object.values(moods).forEach(v => {
          const c = typeof v === 'string' ? v : v?.color;
          if (c) allCounts[c] = (allCounts[c] || 0) + 1;
        });
        const topColor = Object.entries(allCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
        const topLabel = getMoodLabel(topColor);
        // Last 14 days strip
        const last14 = Array.from({ length: 14 }, (_, i) => {
          const dd = new Date(currentDate);
          dd.setDate(dd.getDate() - (13 - i));
          const v = moods[formatDateKey(dd)];
          return typeof v === 'string' ? v : v?.color || null;
        });

        const card = {
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '20px',
          border: '1px solid #E9E9E7',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        };

        return (
          <div style={{ padding: '24px 24px 8px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>

            {/* Insights card */}
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#37352F' }}>Monthly Insights</span>
                <select
                  value={insightMonth}
                  onChange={(e) => setInsightMonth(e.target.value)}
                  style={{
                    fontSize: '12px',
                    color: '#787774',
                    backgroundColor: 'transparent',
                    border: '1px solid #E9E9E7',
                    borderRadius: '6px',
                    padding: '3px 6px',
                    cursor: 'pointer',
                    outline: 'none',
                    fontFamily: 'inherit',
                  }}
                >
                  {monthOptions.map(({ val, label }) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              </div>
              {sortedMonth.length === 0 ? (
                <p style={{ fontSize: '13px', color: '#9B9A97', margin: 0 }}>No moods logged this month yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {sortedMonth.map(([color, count]) => (
                    <div key={color} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                      <span style={{ fontSize: '12px', color: '#787774', width: '80px', flexShrink: 0, textTransform: 'capitalize' }}>
                        {getMoodLabel(color)}
                      </span>
                      <div style={{ flex: 1, height: '6px', borderRadius: '3px', backgroundColor: '#F5F5F4', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: `${(count / maxCount) * 100}%`,
                          backgroundColor: color,
                          borderRadius: '3px',
                          transition: 'width 0.4s ease',
                        }} />
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#37352F', width: '20px', textAlign: 'right', flexShrink: 0 }}>
                        {count}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Trends card */}
            <div style={card}>
              <div style={{ marginBottom: '16px' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#37352F' }}>Trends</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '18px' }}>
                {[
                  { label: 'streak', value: streak > 0 ? `${streak}d` : '—', sub: 'current' },
                  { label: 'logged', value: totalLogged, sub: 'this year' },
                  { label: 'top mood', value: topLabel || '—', sub: 'most felt mood', color: topColor },
                ].map(({ label, value, sub, color: c }) => (
                  <div key={label} style={{
                    backgroundColor: '#F5F5F4',
                    borderRadius: '10px',
                    padding: '10px 8px',
                    textAlign: 'center',
                  }}>
                    <div style={{
                      fontSize: '16px', fontWeight: '700',
                      color: c || '#37352F',
                      textTransform: 'capitalize',
                      marginBottom: '2px',
                      lineHeight: 1.2,
                    }}>
                      {value}
                    </div>
                    <div style={{ fontSize: '10px', color: '#9B9A97', fontWeight: '500' }}>{sub}</div>
                  </div>
                ))}
              </div>
              {/* Last 14 days strip */}
              <div style={{ fontSize: '11px', color: '#9B9A97', marginBottom: '6px', fontWeight: '500' }}>Last 14 days</div>
              <div style={{ display: 'flex', gap: '3px' }}>
                {last14.map((c, i) => (
                  <div key={i} style={{
                    flex: 1,
                    height: '18px',
                    borderRadius: '3px',
                    backgroundColor: c || '#E9E9E7',
                    opacity: c ? 1 : 0.5,
                  }} />
                ))}
              </div>
            </div>

          </div>
        );
      })()}

      {/* Date Detail Modal */}
      {showDateDetail && detailDate && (() => {
        const dateKey = formatDateKey(detailDate);
        const moodData = moods[dateKey];
        const color = typeof moodData === 'string' ? moodData : moodData?.color;
        const note = moodData && typeof moodData === 'object' ? moodData.note : null;
        const label = getMoodLabel(color);
        return (
          <div
            onClick={() => setShowDateDetail(false)}
            style={{
              position: 'fixed', inset: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: '20px',
              animation: 'fadeIn 0.2s ease-out',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                maxWidth: '360px',
                width: '100%',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
                animation: 'slideUp 0.3s ease-out',
              }}
            >
              {/* Color header */}
              <div style={{
                backgroundColor: color || '#E9E9E7',
                padding: '28px 24px 24px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '13px', fontWeight: '500', color: 'rgba(255,255,255,0.85)', marginBottom: '6px' }}>
                  {detailDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#FFFFFF', textTransform: 'capitalize' }}>
                  {label || 'Mood logged'}
                </div>
              </div>
              {/* Note body */}
              <div style={{ padding: '20px 24px 24px' }}>
                {note ? (
                  <p style={{
                    fontSize: '14px', color: '#787774', lineHeight: '1.6',
                    fontStyle: 'italic', margin: '0 0 20px',
                    borderLeft: `3px solid ${color || '#E9E9E7'}`,
                    paddingLeft: '12px',
                  }}>
                    "{note}"
                  </p>
                ) : (
                  <p style={{ fontSize: '13px', color: '#9B9A97', margin: '0 0 20px' }}>No note for this day.</p>
                )}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => { setShowDateDetail(false); handleDayClick(detailDate); }}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '8px',
                      backgroundColor: '#37352F', color: '#FFFFFF',
                      border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setShowDateDetail(false)}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '8px',
                      backgroundColor: '#F5F5F4', color: '#37352F',
                      border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer',
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Mood Picker Modal */}
      {showPicker && selectedDate && (
        <div
          onClick={handleClose}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            animation: 'fadeIn 0.2s ease-out',
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              animation: 'slideUp 0.3s ease-out',
            }}
          >
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#37352F',
              marginBottom: '4px',
            }}>
              How was {selectedDate.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric',
                year: 'numeric'
              })}?
            </h3>
            <p style={{
              fontSize: '13px',
              color: '#787774',
              marginBottom: '20px',
            }}>
              Choose a color that represents your mood.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '20px',
            }}>
              {moodColors.map((mood) => (
                <div
                  key={mood.name}
                  onClick={() => handleColorSelect(mood.color)}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    border: selectedColor === mood.color ? '2px solid #37352F' : '1px solid #E9E9E7',
                    transition: 'all 0.15s ease',
                    backgroundColor: selectedColor === mood.color ? '#F5F5F4' : '#FAFAFA',
                  }}
                  onMouseEnter={(e) => {
                    if (selectedColor !== mood.color) {
                      e.currentTarget.style.backgroundColor = '#F1F1EF';
                      e.currentTarget.style.borderColor = '#D3D3D1';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedColor !== mood.color) {
                      e.currentTarget.style.backgroundColor = '#FAFAFA';
                      e.currentTarget.style.borderColor = '#E9E9E7';
                    }
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: mood.color,
                    flexShrink: 0,
                    border: '1px solid rgba(0,0,0,0.1)',
                  }} />
                  <span style={{
                    fontSize: '14px',
                    color: '#37352F',
                    fontWeight: selectedColor === mood.color ? '500' : '400',
                  }}>
                    {mood.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Notes Section */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}>
                <label style={{
                  fontSize: '13px',
                  fontWeight: '500',
                  color: '#37352F',
                }}>
                  Notes:
                </label>
                <span style={{
                  fontSize: '11px',
                  color: currentNote.split(/\s+/).filter(w => w).length > 75 ? '#E03E3E' : '#9B9A97',
                }}>
                  {currentNote.split(/\s+/).filter(w => w).length}/75 words
                </span>
              </div>
              <div style={{ position: 'relative' }}>
                <textarea
                  value={currentNote}
                  onChange={(e) => setCurrentNote(e.target.value)}
                  placeholder="Add a short note about your day..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '10px',
                    fontSize: '13px',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                    border: '1px solid #E9E9E7',
                    borderRadius: '6px',
                    resize: 'vertical',
                    color: '#37352F',
                    backgroundColor: '#FAFAFA',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#37352F';
                    e.currentTarget.style.backgroundColor = '#FFFFFF';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#E9E9E7';
                    e.currentTarget.style.backgroundColor = '#FAFAFA';
                  }}
                />
                <button
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#F5F5F4';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  😊
                </button>
              </div>
              
              {showEmojiPicker && (
                <div style={{
                  marginTop: '8px',
                  padding: '12px',
                  backgroundColor: '#FAFAFA',
                  border: '1px solid #E9E9E7',
                  borderRadius: '6px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(9, 1fr)',
                  gap: '8px',
                  maxHeight: '120px',
                  overflowY: 'auto',
                }}>
                  {commonEmojis.map((emoji, index) => (
                    <button
                      key={index}
                      onClick={() => addEmoji(emoji)}
                      style={{
                        fontSize: '20px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#E9E9E7';
                        e.currentTarget.style.transform = 'scale(1.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '12px',
            }}>
              <button
                onClick={handleClear}
                style={{
                  backgroundColor: 'transparent',
                  color: '#787774',
                  border: '1px solid #E9E9E7',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F5F5F4';
                  e.currentTarget.style.borderColor = '#D3D3D1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = '#E9E9E7';
                }}
              >
                Clear
              </button>
              <button
                onClick={handleSave}
                disabled={!selectedColor}
                style={{
                  backgroundColor: !selectedColor ? '#E9E9E7' : '#37352F',
                  color: !selectedColor ? '#9B9A97' : '#FFFFFF',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: !selectedColor ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  if (selectedColor) {
                    e.currentTarget.style.backgroundColor = '#1F1E1B';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedColor) {
                    e.currentTarget.style.backgroundColor = '#37352F';
                  }
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      {renderBottomNav('home')}
      {renderMockBtn()}
    </div>
  );
};

export default MoodYearRecap;
